// src/routing/authed-gate.tsx
import React, { useCallback, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/auth/auth-context';
import { AppLoader } from '../components/app-loader/app-loader';
import { useAppBoot } from '../contexts/app-boot-context';
import { usePayment } from '../contexts/payment-context';
import { TrialExpiredScreen } from '../components/trial-expired/trial-expired';
import { readStoredTokens } from '../contexts/auth/helpers';

const authedTargetPath = (profile: any): '/' | '/onboarding' | '/chat' => {
  const registrationCompleted = profile?.registration_completed === true;
  const onboardingCompleted = profile?.onboarding_completed === true;

  if (!registrationCompleted && !onboardingCompleted) return '/';
  if (registrationCompleted && !onboardingCompleted) return '/onboarding';
  if (registrationCompleted && onboardingCompleted) return '/chat';

  return '/';
};

export const AuthedGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile } = useAuth();
  const { authed, bootLoading } = useAppBoot();
  const location = useLocation();

  // After the first successful boot, never show the splash screen again.
  // Post-boot loading flutters (proactive token refresh, visibility change,
  // payment refresh on focus, etc.) must NOT unmount children — doing so
  // kills in-progress work like audio recordings on the onboarding page.
  const initialBootDoneRef = useRef(false);
  if (!bootLoading) initialBootDoneRef.current = true;

  if (bootLoading && !initialBootDoneRef.current) {
    return <AppLoader active={true} label="Loading your workspace" />;
  }

  // Defence against stale `authed` memo: if React state says not-authed
  // but valid tokens exist in storage AND we have a loaded profile,
  // treat the user as authenticated (the useMemo simply hasn't recalculated yet).
  const effectiveAuthed = authed || (() => {
    if (!profile) return false;
    const tokens = readStoredTokens();
    return !!tokens.access_token && !!tokens.refresh_token;
  })();

  if (!effectiveAuthed) {
    const redirectParam = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirectParam}`} replace />;
  }

  if (!profile) {
    // Boot completed but profile failed to load — session is likely invalid.
    // Redirect to login so the user can re-authenticate.
    const redirectParam = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirectParam}`} replace />;
  }

  const baseTarget = authedTargetPath(profile);

  // welcome gating (kept)
  const userId = profile?.id as string | undefined;
  const welcomeSeenKey = userId ? `onboarding_welcome_seen_${userId}` : null;
  const welcomeSeen = (() => {
    if (!welcomeSeenKey) return false;
    try {
      return localStorage.getItem(welcomeSeenKey) === 'true';
    } catch {
      return false;
    }
  })();

  let target = baseTarget;
  if (baseTarget === '/onboarding') {
    target = welcomeSeen ? '/chat' : '/onboarding';
  }

  // If we're already at the target, gate on payment status before rendering
  if (location.pathname === target) {
    return <PaymentGatedContent>{children}</PaymentGatedContent>;
  }

  return <Navigate to={target} replace />;
};

/**
 * Inner component that gates on payment/trial status.
 * Shown only after auth/profile/onboarding checks pass.
 */
const PaymentGatedContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { paymentLoading, canAccessApp, fetchError, paymentInfo, refreshPayment } = usePayment();

  const handleOpenBilling = useCallback(() => {
    // Dispatch a custom event that the Sidebar listens for to open Settings > Billing
    window.dispatchEvent(new CustomEvent('open-settings-billing'));
  }, []);

  // Same pattern as AuthedGate: only show the payment splash on the very
  // first check. Subsequent refreshes (e.g. window focus after 5 min)
  // must NOT unmount children — that kills in-progress recordings.
  const initialCheckDoneRef = useRef(false);
  if (!paymentLoading) initialCheckDoneRef.current = true;

  if (paymentLoading && !initialCheckDoneRef.current) {
    return <AppLoader active={true} label="Checking subscription" />;
  }

  // Render children + lockout overlay so Settings modal can open on top (z-index 1002 > 900)
  return (
    <>
      {children}
      {fetchError && !paymentInfo && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1001,
            background: '#2b2d33',
            padding: '12px 20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span style={{ color: '#E1C27A', fontSize: '14px', fontFamily: 'Inter' }}>
            Failed to load subscription info.
          </span>
          <button
            onClick={refreshPayment}
            style={{
              color: '#5E84ED',
              background: 'transparent',
              border: 'none',
              fontSize: '14px',
              fontFamily: 'Inter',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}
      {!canAccessApp && !fetchError && <TrialExpiredScreen onOpenBilling={handleOpenBilling} />}
    </>
  );
};
